import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useSearch } from "wouter";
import { CheckCircle, Download, ArrowRight, FileIcon } from "lucide-react";
import { formatOrderNumber } from "@shared/orderUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { SEO } from "@/components/seo";

type DownloadFileEntry = {
  url: string;
  name: string;
};

interface OrderDetail {
  id: number;
  email: string;
  total: string;
  status: string;
  createdAt: string;
  items: Array<{
    id: number;
    productId: number;
    price: string;
    product: {
      id: number;
      name: string;
      slug: string;
      image: string | null;
      downloadFile: string | null;
      downloadFiles: string[] | null;
      instructions: string | null;
      instructionImages: string[] | null;
      howToUse: string | null;
    };
  }>;
}

export default function OrderConfirmation() {
  const { id } = useParams<{ id: string }>();
  const searchString = useSearch();
  const code = new URLSearchParams(searchString).get("code") || "";

  const { data: order, isLoading } = useQuery<OrderDetail>({
    queryKey: ["/api/orders", `${id}?code=${code}`],
    enabled: !!code,
  });

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <Skeleton className="w-16 h-16 rounded-full mx-auto mb-4" />
          <Skeleton className="h-8 w-64 mx-auto mb-2" />
          <Skeleton className="h-5 w-96 mx-auto" />
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-20 w-full mb-4" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Order Not Found</h1>
        <p className="text-muted-foreground mb-4">
          We couldn't find this order. Please check the order number.
        </p>
        <Link href="/">
          <Button>Back to Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Order Confirmed" description="Your Sierra Chart template order has been confirmed. Download your files below." noIndex />
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="text-order-success">
          Order Confirmed!
        </h1>
        <p className="text-muted-foreground">
          {formatOrderNumber(order.id)} &middot; {order.email}
        </p>
      </div>

      <Card className="mb-6">
        <CardContent className="p-6">
          <h2 className="font-semibold text-lg mb-4">Your Downloads</h2>
          <div className="space-y-6">
            {order.items.map((item) => {
              const rawFiles = item.product.downloadFiles || [];
              const downloadFiles: DownloadFileEntry[] = rawFiles.map((f) => {
                try {
                  const parsed = JSON.parse(f);
                  if (parsed && typeof parsed === "object" && parsed.url) return parsed;
                } catch {}
                return { url: f, name: f.split("/").pop() || "file" };
              });
              const hasFiles = downloadFiles.length > 0;

              return (
                <div key={item.id} data-testid={`order-item-${item.id}`}>
                  <div className="flex items-center gap-4">
                    {item.product.image && (
                      <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{item.product.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        ${Number(item.price).toFixed(2)}
                      </p>
                    </div>
                    {!hasFiles && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = `/api/orders/${order.id}/download/${item.productId}?code=${code}`;
                          link.download = "";
                          link.click();
                        }}
                        data-testid={`button-download-${item.productId}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    )}
                  </div>
                  {hasFiles && (
                    <div className="mt-3 ml-20 space-y-2">
                      {downloadFiles.map((df, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <FileIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                            <span className="text-sm truncate">{df.name}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = `/api/orders/${order.id}/download/${item.productId}?code=${code}&fileIndex=${idx}`;
                              link.download = "";
                              link.click();
                            }}
                            data-testid={`button-download-${item.productId}-file-${idx}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {(item.product.instructions || (item.product.instructionImages && item.product.instructionImages.length > 0)) && (
                    <div className="mt-3 ml-20 p-3 rounded-md bg-muted/50">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Instructions</p>
                      {item.product.instructions && (
                        <p className="text-sm whitespace-pre-wrap" data-testid={`text-instructions-${item.productId}`}>{item.product.instructions}</p>
                      )}
                      {item.product.instructionImages && item.product.instructionImages.length > 0 && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {item.product.instructionImages.map((img, idx) => (
                            <a key={idx} href={img} target="_blank" rel="noopener noreferrer" data-testid={`instruction-image-${item.productId}-${idx}`}>
                              <img src={img} alt={`Instruction step ${idx + 1}`} className="rounded-md border w-full" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {item.product.howToUse && (
                    <div className="mt-3 ml-20 p-3 rounded-md border border-primary/20 bg-primary/5">
                      <p className="text-xs font-semibold text-primary mb-1">How to Use</p>
                      <p className="text-sm whitespace-pre-wrap" data-testid={`text-how-to-use-${item.productId}`}>{item.product.howToUse}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="p-6">
          <h2 className="font-semibold text-lg mb-4">Order Details</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Order Number</span>
              <span className="font-medium">{formatOrderNumber(order.id)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{order.email}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Status</span>
              <Badge variant="secondary">{order.status}</Badge>
            </div>
            <Separator />
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">Total</span>
              <span className="text-lg font-bold text-primary">
                ${Number(order.total).toFixed(2)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <Link href="/">
          <Button data-testid="button-continue-shopping">
            Continue Shopping
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
